import React, { useState, useMemo } from "react";
import { PhoneLink } from "@/utils/PhoneLink";
import {
  Box, Typography, TextField, Button, CircularProgress, Alert,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  Chip, Tooltip, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CloseIcon from "@mui/icons-material/Close";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { phappsRequest, phappsBlob } from "@/services/apiService";
import { API_PATHS } from "@/config/apiConstants";
import ZephyrIconWhite from "@/Images/Icons/Zephyr_Icon_White.png";
import WriteOffButton from "@/components/zephyr/WriteOffButton";
import EditableInvAmount from "@/components/zephyr/EditableInvAmount";

const Z_PURPLE = "#38184f";
const Z_BLUE = "#2baede";
const fmt = (v) => new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" }).format(Number(v ?? 0));
const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-AU") : "—";

function scriptRowBg(s) {
  if (s.PaymentID) return "#dcfce7";
  if (Number(s.WrittenOff)) return "#fee2e2";
  if (Number(s.OnHold) === -1) return "#fef9c3";
  return "transparent";
}

function ScriptRow({ s, claimID, onOpenPayment, onWriteOff, onPriceError }) {
  const [open, setOpen] = useState(false);

  const statusCell = () => {
    if (s.PaymentID) return (
      <Chip
        label={`Paid #${s.PaymentID}`}
        color="success"
        size="small"
        onClick={() => onOpenPayment(s.PaymentID)}
        sx={{ fontSize: "0.72rem", cursor: "pointer" }}
      />
    );
    if (Number(s.WrittenOff)) return <Chip label="Written Off" color="error" size="small" sx={{ fontSize: "0.72rem" }} />;
    if (Number(s.OnHold) === -1) return <Chip label="Missed" color="warning" size="small" sx={{ fontSize: "0.72rem" }} />;
    return null;
  };

  return (
    <>
      <TableRow sx={{ backgroundColor: scriptRowBg(s), "& td": { py: 0.75, px: 1.5, fontSize: "0.82rem" } }}>
        <TableCell>{s.ScriptID}</TableCell>
        <TableCell>{fmtDate(s.DateDispensed)}</TableCell>
        <TableCell>{s.txtItem}</TableCell>
        <TableCell align="center">{s.Qty ?? "—"}</TableCell>
        <TableCell align="right">
          <EditableInvAmount script={s} onSaved={onWriteOff} onError={onPriceError} />
        </TableCell>
        <TableCell align="right">{s.RcvdAmount != null ? fmt(s.RcvdAmount) : "—"}</TableCell>
        <TableCell align="center">{statusCell()}</TableCell>
        <TableCell>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Tooltip title="Show details">
              <IconButton size="small" onClick={() => setOpen((o) => !o)} sx={{ p: 0.5 }}>
                {open ? <ExpandLessIcon fontSize="small" /> : <InfoOutlinedIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
            <WriteOffButton
              claimID={claimID}
              scriptIDs={[s.ScriptID]}
              scripts={[s]}
              onSuccess={onWriteOff}
            />
          </Box>
        </TableCell>
      </TableRow>
      {open && (
        <TableRow sx={{ backgroundColor: scriptRowBg(s) || "#f8fafc" }}>
          <TableCell colSpan={8} sx={{ py: 1.5, px: 2.5 }}>
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5, maxWidth: 700 }}>
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={700}>Batch Code</Typography>
                <Typography variant="body2">{s.ScrBatchID || "—"}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={700}>Prescriber</Typography>
                <Typography variant="body2">{s.PrescriberName || "—"}</Typography>
              </Box>
              {s.Directions && (
                <Box sx={{ gridColumn: "1 / -1" }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={700}>Directions</Typography>
                  <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>{s.Directions}</Typography>
                </Box>
              )}
              {s.ReconcilID && (
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={700}>Statement</Typography>
                  <Typography variant="body2">Stmt #{s.ReconcilID}</Typography>
                </Box>
              )}
            </Box>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function PaymentModal({ open, paymentID, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (!open || !paymentID) return;
    setLoading(true);
    setData(null);
    phappsRequest(`${API_PATHS.ZEPHYR_ADMIN_PAYMENT_DETAILS}/${paymentID}`, "GET")
      .then((res) => setData(res?.data ?? null))
      .finally(() => setLoading(false));
  }, [open, paymentID]);

  const h = data?.header;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}>
      <DialogTitle sx={{
        background: `linear-gradient(135deg, ${Z_PURPLE}, ${Z_BLUE})`, color: "#fff",
        display: "flex", alignItems: "center", justifyContent: "space-between", py: 1.5,
      }}>
        <span>Payment #{paymentID}</span>
        <IconButton onClick={onClose} size="small" sx={{ color: "#fff" }}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        {loading ? <CircularProgress sx={{ color: Z_PURPLE, mt: 2 }} /> : (
          <>
            {h && (
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 1.5, mb: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={700}>Pay Date</Typography>
                  <Typography variant="body2">{fmtDate(h.PayDate)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={700}>Total Value</Typography>
                  <Typography variant="body2" fontWeight={700} sx={{ color: "#16a34a" }}>{fmt(h.TotalPaymentValue)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={700}>Reference</Typography>
                  <Typography variant="body2">{h.ReferenceNumber || "—"}</Typography>
                </Box>
                {h.PaymentComment && (
                  <Box sx={{ gridColumn: "1 / -1" }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={700}>Comment</Typography>
                    <Typography variant="body2">{h.PaymentComment}</Typography>
                  </Box>
                )}
              </Box>
            )}
            <TableContainer sx={{ border: "1px solid #e2e8f0", borderRadius: 1 }}>
              <Table size="small">
                <TableHead sx={{ backgroundColor: `${Z_PURPLE}08` }}>
                  <TableRow>
                    {["Patient", "Invoice", "Item", "Invoiced", "Received"].map((col) => (
                      <TableCell key={col} sx={{ fontWeight: 700, fontSize: "0.78rem", color: Z_PURPLE }}>{col}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(data?.scripts ?? []).map((s) => (
                    <TableRow key={s.ScriptID} sx={{ "& td": { py: 0.6, px: 1, fontSize: "0.8rem" } }}>
                      <TableCell>{s.FullName}</TableCell>
                      <TableCell>{s.InvoiceID}</TableCell>
                      <TableCell>{s.txtItem}</TableCell>
                      <TableCell align="right">{fmt(s.InvAmount)}</TableCell>
                      <TableCell align="right">{fmt(s.RcvdAmount)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

function InvoicePdfModal({ open, invoiceID, onClose }) {
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  React.useEffect(() => {
    if (!open || !invoiceID) return;
    let cancelled = false;
    let objUrl = null;
    setLoading(true);
    setError(null);
    setUrl(null);
    phappsBlob(`${API_PATHS.ZEPHYR_ADMIN_INVOICE_PDF}/${invoiceID}`)
      .then((blob) => {
        if (cancelled) return;
        objUrl = URL.createObjectURL(blob);
        setUrl(objUrl);
      })
      .catch(() => !cancelled && setError("Failed to load invoice PDF."))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
      if (objUrl) URL.revokeObjectURL(objUrl);
    };
  }, [open, invoiceID]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: "hidden", height: "90vh" } }}>
      <DialogTitle sx={{
        background: `linear-gradient(135deg, ${Z_PURPLE}, ${Z_BLUE})`, color: "#fff",
        display: "flex", alignItems: "center", justifyContent: "space-between", py: 1.5,
      }}>
        <span>Invoice #{invoiceID}</span>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          {url && (
            <Tooltip title="Open in new tab">
              <IconButton component="a" href={url} target="_blank" rel="noopener noreferrer"
                size="small" sx={{ color: "#fff" }}>
                <OpenInNewIcon />
              </IconButton>
            </Tooltip>
          )}
          <IconButton onClick={onClose} size="small" sx={{ color: "#fff" }}><CloseIcon /></IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 0, display: "flex", flexDirection: "column" }}>
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", flex: 1 }}>
            <CircularProgress sx={{ color: Z_PURPLE }} />
          </Box>
        )}
        {error && <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>}
        {url && !loading && (
          <iframe title={`Invoice ${invoiceID}`} src={url}
            style={{ border: "none", flex: 1, width: "100%" }} />
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function ZephyrInvoicesPage() {
  const [search, setSearch] = useState("");
  const [invoiceData, setInvoiceData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [paymentModal, setPaymentModal] = useState(null);
  const [pdfOpen, setPdfOpen] = useState(false);
  const [priceError, setPriceError] = useState(null);

  const loadInvoice = (id) => {
    setLoading(true);
    setError(null);
    setInvoiceData(null);
    phappsRequest(`${API_PATHS.ZEPHYR_ADMIN_INVOICE_DETAILS}/${id}`, "GET")
      .then((res) => setInvoiceData(res?.data ?? null))
      .catch(() => setError("Invoice not found or failed to load."))
      .finally(() => setLoading(false));
  };

  const doSearch = () => {
    const id = parseInt(search.trim(), 10);
    if (!id) return;
    loadInvoice(id);
  };

  const doRefresh = () => {
    if (invoiceData?.invoiceID) loadInvoice(invoiceData.invoiceID);
  };

  const totals = useMemo(() => {
    if (!invoiceData?.scripts?.length) return null;
    let paid = 0, outstanding = 0, writtenOff = 0, pending = 0;
    for (const s of invoiceData.scripts) {
      const amt = Number(s.InvAmount ?? 0);
      if (Number(s.WrittenOff)) writtenOff += amt;
      else if (s.PaymentID) paid += Number(s.RcvdAmount ?? 0);
      else if (Number(s.OnHold) === -1) pending += amt;
      else outstanding += amt;
    }
    return { paid, outstanding, writtenOff, pending };
  }, [invoiceData]);

  const c = invoiceData?.claim;

  return (
    <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <Box sx={{ width: 36, height: 36, borderRadius: 1.5,
          background: `linear-gradient(135deg, ${Z_PURPLE}, ${Z_BLUE})`,
          display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <img src={ZephyrIconWhite} alt="" style={{ height: 20, objectFit: "contain" }} />
        </Box>
        <Typography fontWeight={800} fontSize="1.25rem" sx={{ color: Z_PURPLE }}>Invoices</Typography>
      </Box>

      {/* Search bar */}
      <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
        <TextField
          label="Invoice Number"
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && doSearch()}
          sx={{ width: 200 }}
          autoFocus
        />
        <Button
          variant="contained"
          startIcon={loading ? null : <SearchIcon />}
          onClick={doSearch}
          disabled={loading}
          sx={{
            textTransform: "none", fontWeight: 700,
            background: `linear-gradient(135deg, ${Z_PURPLE}, ${Z_BLUE})`,
            "&:hover": { background: `linear-gradient(135deg, #1a0a2e, ${Z_PURPLE})` },
          }}
        >
          {loading ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Search"}
        </Button>
      </Box>

      {/* Invoice details card — centered, fixed width */}
      {c && (
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <Box sx={{
            background: `linear-gradient(135deg, ${Z_PURPLE}0d, ${Z_BLUE}08)`,
            border: `1px solid ${Z_PURPLE}28`,
            borderRadius: 2,
            p: 2,
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "10px 32px",
            minWidth: 420,
            maxWidth: 620,
            width: "100%",
          }}>
            {/* Row 0: Invoice # | Date */}
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={700} display="block">Invoice</Typography>
              <Typography fontWeight={800} sx={{ color: Z_PURPLE }}>#{invoiceData.invoiceID}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={700} display="block">Date</Typography>
              <Typography variant="body2">{fmtDate(c.InvoiceDate)}</Typography>
            </Box>

            {/* Row 1: Patient | Claim Number */}
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={700} display="block">Patient</Typography>
              <Typography variant="body2" fontWeight={700}>{c.FullName || "—"}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={700} display="block">Claim Number</Typography>
              <Typography variant="body2">{c.ClaimNumber || "—"}</Typography>
            </Box>

            {/* Row 2: Invoiced To (full width) */}
            <Box sx={{ gridColumn: "1 / -1", borderTop: "1px solid #e2e8f0", pt: 1 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={700} display="block">Invoiced To</Typography>
              <Typography variant="body2" fontWeight={600}>{c.InsCompName || c.EmployerName || "—"}</Typography>
            </Box>

            {/* Row 3: Chemist | Phone + Email */}
            {(c.ChemistName || c.ChemShortCode) && (
              <>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={700} display="block">Chemist</Typography>
                  <Typography variant="body2" fontWeight={600}>{c.ChemistName || c.ChemShortCode}</Typography>
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "flex-end", gap: 0.5 }}>
                  {c.ChemistPhone && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <PhoneOutlinedIcon sx={{ fontSize: 13, color: "#64748b" }} />
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.8rem" }}>
                        <PhoneLink phone={c.ChemistPhone} />
                      </Typography>
                    </Box>
                  )}
                  {c.ChemistEmail && (
                    <Button size="small" startIcon={<EmailOutlinedIcon sx={{ fontSize: 13 }} />}
                      href={`mailto:${c.ChemistEmail}`}
                      sx={{ textTransform: "none", color: Z_BLUE, p: 0, minWidth: 0, fontSize: "0.75rem", justifyContent: "flex-start" }}>
                      Email chemist
                    </Button>
                  )}
                </Box>
              </>
            )}
          </Box>
        </Box>
      )}

      {error && <Alert severity="error">{error}</Alert>}

      {invoiceData && !c && (
        <Alert severity="warning">Invoice found but no claim data returned.</Alert>
      )}

      {invoiceData && c && (
        <>
          {/* Totals + invoice comment inline */}
          {totals && (
            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center" }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<PictureAsPdfIcon />}
                onClick={() => setPdfOpen(true)}
                sx={{
                  textTransform: "none", fontWeight: 700,
                  color: Z_PURPLE, borderColor: `${Z_PURPLE}55`,
                  "&:hover": { borderColor: Z_PURPLE, background: `${Z_PURPLE}08` },
                }}
              >
                View Invoice
              </Button>
              <WriteOffButton
                claimID={c.ClaimID}
                invoiceID={invoiceData.invoiceID}
                scripts={invoiceData.scripts}
                onSuccess={doRefresh}
              />
              {totals.paid > 0 && (
                <Chip label={`Paid: ${fmt(totals.paid)}`} sx={{ background: "#dcfce7", color: "#16a34a", fontWeight: 700 }} />
              )}
              {totals.outstanding > 0 && (
                <Chip label={`Outstanding: ${fmt(totals.outstanding)}`} sx={{ background: "#fee2e2", color: "#dc2626", fontWeight: 700 }} />
              )}
              {totals.pending > 0 && (
                <Chip label={`Missed: ${fmt(totals.pending)}`} sx={{ background: "#fef9c3", color: "#92400e", fontWeight: 700 }} />
              )}
              {totals.writtenOff > 0 && (
                <Chip label={`Written Off: ${fmt(totals.writtenOff)}`} sx={{ background: "#f1f5f9", color: "#64748b", fontWeight: 700 }} />
              )}
              {c.InvComment && (
                <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5, pl: 0.5 }}>
                  <Typography variant="body2" sx={{ fontSize: "0.82rem", color: "#475569", fontStyle: "italic" }}>
                    {c.InvComment}
                  </Typography>
                  {c.InvCommDate && (
                    <Typography variant="caption" color="text.secondary">
                      — {fmtDate(c.InvCommDate)}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          )}

          {priceError && (
            <Alert severity="warning" onClose={() => setPriceError(null)} sx={{ mb: 1.5 }}>
              {priceError}
            </Alert>
          )}
          <TableContainer sx={{ border: "1px solid #e2e8f0", borderRadius: 1.5, background: "#fff" }}>
            <Table size="small">
              <TableHead sx={{ backgroundColor: `${Z_PURPLE}08` }}>
                <TableRow>
                  {[
                    { label: "Script ID", align: "left" },
                    { label: "Dispensed", align: "left" },
                    { label: "Item", align: "left" },
                    { label: "Qty", align: "center" },
                    { label: "Invoiced", align: "right" },
                    { label: "Received", align: "right" },
                    { label: "Status", align: "center" },
                    { label: "", align: "left" },
                  ].map(({ label, align }) => (
                    <TableCell key={label} align={align} sx={{ fontWeight: 700, fontSize: "0.78rem", color: Z_PURPLE, py: 1 }}>
                      {label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {invoiceData.scripts.map((s) => (
                  <ScriptRow key={s.ScriptID} s={s} claimID={c.ClaimID} onOpenPayment={setPaymentModal} onWriteOff={doRefresh} onPriceError={setPriceError} />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      <PaymentModal
        open={!!paymentModal}
        paymentID={paymentModal}
        onClose={() => setPaymentModal(null)}
      />

      <InvoicePdfModal
        open={pdfOpen}
        invoiceID={invoiceData?.invoiceID}
        onClose={() => setPdfOpen(false)}
      />
    </Box>
  );
}
